import React, { useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Backdrop,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
} from "@mui/material";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  deleteBuild,
  fetchBuild,
  promoteBuildToHot,
  upsertBuild,
} from "../../../redux/actions/buildActions";
import BulkDeploymentPanel from "./BulkDeploymentPanel";

interface BuildEntry {
  platform: string;
  repository_type: string;
  key: string;
}

const BuildView: React.FC = () => {
  const { buildId } = useParams<{ buildId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { currentBuild, loading, error } = useSelector(
    (state: RootState) => state.builds
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isMakingHot, setIsMakingHot] = React.useState(false);
  const [buildNotes, setBuildNotes] = React.useState("");
  const [isEditingNotes, setIsEditingNotes] = React.useState(false);
  const [isSavingNotes, setIsSavingNotes] = React.useState(false);

  const handleDeleteBuild = async () => {
    if (!buildId) {
      toast.error("Build ID is required to delete the build.");
      return;
    }

    try {
      setIsDeleting(true);
      await dispatch(deleteBuild(buildId)).unwrap();
      toast.success("Build deleted successfully!");
      navigate("/dashboard/builds");
    } catch (err) {
      toast.error("Failed to delete build.");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleMakeHot = async () => {
    if (!buildId) {
      toast.error("Build ID is required.");
      return;
    }
    try {
      setIsMakingHot(true);
      await dispatch(promoteBuildToHot(buildId)).unwrap();
      toast.success("Build marked as hot. Syncing artifacts…");
      await dispatch(fetchBuild(buildId));
    } catch (err: any) {
      toast.error("Failed to make build hot.");
    } finally {
      setIsMakingHot(false);
    }
  };

  const handleSaveBuildNotes = async () => {
    if (!currentBuild || !buildId) {
      toast.error("Build data is required to save notes.");
      return;
    }
    try {
      setIsSavingNotes(true);
      await dispatch(
        upsertBuild({
          id: currentBuild.id,
          build_name: currentBuild.build_name,
          entries: currentBuild.entries,
          build_notes: buildNotes || null,
        })
      ).unwrap();
      toast.success("Build notes saved successfully!");
      setIsEditingNotes(false);
      await dispatch(fetchBuild(buildId));
    } catch (err: any) {
      toast.error("Failed to save build notes.");
    } finally {
      setIsSavingNotes(false);
    }
  };

  useEffect(() => {
    if (buildId) {
      dispatch(fetchBuild(buildId));
    }
  }, [dispatch, buildId]);

  useEffect(() => {
    if (currentBuild) {
      setBuildNotes(currentBuild.build_notes || "");
    }
  }, [currentBuild]);

  useEffect(() => {
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  if (loading && !currentBuild) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentBuild) {
    return <div>Build not found</div>;
  }

  const isDeleted = Boolean(currentBuild.deleted_at || false);

  return (
    <Box sx={{ p: 4, maxWidth: 1200 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Build Details
      </Typography>

      {isDeleted && (
        <Alert severity="error" sx={{ mb: 3 }}>
          This build has been deleted.
        </Alert>
      )}

      {/* Build Information Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Build ID"
                value={currentBuild.id}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: "grey.50",
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Build Name"
                value={currentBuild.build_name}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    backgroundColor: "grey.50",
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  Status:
                </Typography>
                {currentBuild.s3_build_is_hot ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Hot Build"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label="Not Hot"
                    color="default"
                    size="small"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Build Notes Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Build Notes
          </Typography>
          <TextField
            value={buildNotes}
            onChange={(e) => setBuildNotes(e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="Add optional notes about this build..."
            InputProps={{
              readOnly: !isEditingNotes,
              sx: {
                backgroundColor: isEditingNotes
                  ? "background.paper"
                  : "grey.50",
              },
            }}
            helperText="Optional notes about this build"
          />
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            {!isEditingNotes ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setIsEditingNotes(true)}
                disabled={isDeleted || isMakingHot || isSavingNotes}
              >
                Edit Notes
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveBuildNotes}
                  disabled={isSavingNotes || isMakingHot}
                  startIcon={
                    isSavingNotes ? <CircularProgress size={18} /> : undefined
                  }
                >
                  {isSavingNotes ? "Saving..." : "Save Notes"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setBuildNotes(currentBuild.build_notes || "");
                    setIsEditingNotes(false);
                  }}
                  disabled={isSavingNotes || isMakingHot}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Build Entries Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Build Entries ({currentBuild.entries?.length || 0})
          </Typography>
          <List disablePadding>
            {currentBuild.entries?.map((entry: BuildEntry, index: number) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight="medium">
                        {entry.platform}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Repository Type:</strong>{" "}
                          {entry.repository_type}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          <strong>Key:</strong> {entry.key}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
            {(!currentBuild.entries || currentBuild.entries.length === 0) && (
              <ListItem>
                <ListItemText
                  primary={
                    <Typography color="text.secondary">
                      No entries found for this build.
                    </Typography>
                  }
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate("/dashboard/builds")}
          disabled={isMakingHot}
        >
          Back
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleMakeHot}
          disabled={isDeleted || currentBuild.s3_build_is_hot || isMakingHot}
          startIcon={isMakingHot ? <CircularProgress size={18} /> : undefined}
        >
          {isMakingHot ? "Making Hot…" : "Make Hot"}
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
          disabled={isDeleted || isMakingHot}
        >
          Delete
        </Button>
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Build</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this build? This action is
            irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteBuild}
            color="error"
            autoFocus
            disabled={isDeleting || isMakingHot}
          >
            {isDeleting ? "Deleting..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isMakingHot}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        aria-live="polite"
        aria-busy={isMakingHot}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body2">Syncing build to hot storage…</Typography>
        </Box>
      </Backdrop>

      {/* Bulk Deployment Panel - only show for hot builds */}
      {currentBuild.s3_build_is_hot && !isDeleted && (
        <>
          <Divider sx={{ my: 4 }} />
          <BulkDeploymentPanel
            buildId={currentBuild.id}
            buildName={currentBuild.build_name}
          />
        </>
      )}
    </Box>
  );
};

export default BuildView;
